import dayjs from 'dayjs';
import { io } from 'socket.io-client';

import { usePlaidLink } from 'react-plaid-link';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useLocation, useNavigate } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import SyncIcon from '@mui/icons-material/Sync';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import { Animation, Autocomplete, CircleLoading, SnackBarAlert } from 'shared';
import { Box, Grid, Button, useTheme, Typography, useMediaQuery, TextField } from '@mui/material';

import {
    startTour,
    TOUR_TYPES,
    goToNextTour,
    completeTour,
    updateTourStep,
    selectIsSkipped,
    selectTourStatus,
    startTourManually,
    selectIsCompleted,
    selectTutorialPreference,
} from 'store/slices/tour.slice';
import {
    setAccessToken,
    getBankAccounts,
    syncTransactions,
    getBankSyncStatus,
    generateLinkToken,
    syncInitialTransactions,
} from 'store/services/bank.Service';
import { snackAlertData } from 'utils/constants';
import DivvyModal from './Components/DivvyModal';
import { accountTypes } from './utils/accountTypes';
import ManualBankForm from './Components/ManualBankForm';
import BankCardCarousel from './Components/BanksCarousel';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { syncBankAccountsMakeStyles } from './style/style';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import AllBanksUpdateForm from './Components/AllBanksUpdateForm';
import JoyrideTour from 'components/Onboarding/Tour/JoyRideTour';
import AddBankStartDateModal from './Components/AddBankStartDateModal';
import BankTransactionsStack from './Components/BankTransactionsStack';
import ManageConnectionsModal from './Components/ManageConnectionsModal';

const TOKEN = process.env.REACT_APP_SOCKET_TOKEN;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

dayjs.extend(isSameOrAfter);
const todayDate = dayjs(new Date()).format('YYYY-MM-DD');
const yesterdayDate = dayjs(todayDate).subtract(1, 'day');

const steps = [
    {
        target: '.bank-dropdown-container',
        content: 'Click here to open the dropdown',
        placement: 'bottom',
        disableBeacon: false,
    },
    {
        target: '.bank-dropdown-container .MuiAutocomplete-listbox .MuiAutocomplete-option:nth-child(2)',
        content:
            ' Now click “Connect to a Bank Account” to start importing your bank activity through Plaid automatically',
        placement: 'right',
        disableBeacon: false,
    },
    {
        target: '.bank-dropdown-container .MuiAutocomplete-listbox .MuiAutocomplete-option:nth-child(3)',
        content:
            'For manual csv upload for bank accounts not supported through Plaid, Click “Create Manual Bank Account”',
        placement: 'right',
        disableBeacon: false,
    },
];

const ImportBankTransaction = ({ }) => {
    const location = useLocation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const classes = syncBankAccountsMakeStyles({});
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { data: companyData } = useSelector((state) => state?.company);

    const tourId = TOUR_TYPES.BANK_DROPDOWN;
    const nextTourId = TOUR_TYPES.BANK_ACCOUNT;
    const tourStatus = useSelector((state) => selectTourStatus(state, tourId));
    const tutorialPreference = useSelector((state) => selectTutorialPreference(state));
    const isSkipped = useSelector((state) => selectIsSkipped(state, tourId));

    const isNextTourCompleted = useSelector((state) => selectIsCompleted(state, nextTourId));

    const [tourRun, setTourRun] = useState(true);
    const [accounts, setAccounts] = useState(null);
    const [bankOption, setBankOption] = useState(null);
    const [linkToken2, setLinkToken2] = useState(null);
    const [selectedBank, setSelectedBank] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [syncAllLoading, setSyncAllLoading] = useState(false);
    const [showDivvyModal, setShowDivvyModal] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(yesterdayDate);
    const [isAddDateModalOpen, setAddDateModalOpen] = useState(false);
    const [bankAccountsLoading, setBankAccountsLoading] = useState(false);
    const [isCreateLinkLoading, setIsCreateLinkLoading] = useState(false);
    const [showManualAccountForm, setShowManualAccountForm] = useState(false);
    const [showAllBanksUpdateForm, setShowAllBanksUpdateForm] = useState(false);
    const [showManageConnectionsModal, setShowManageConnectionsModal] = useState(false);

    const handleAddDateModalClose = () => {
        setBankOption(null);
        setAddDateModalOpen(false);
    };

    const handleAddDateModalOpen = () => {
        setAddDateModalOpen(true);
        setSelectedDate(yesterdayDate);
    };

    const handleGetBankSyncStatus = async () => {
        try {
            const response = await getBankSyncStatus(companyData?.id);
            setSyncAllLoading(response.sync_status == 'in-progress');
        } catch (error) {
            handleErrorAlert(setAlertData, 'Something went wrong in syncing, please try again');
            setSyncAllLoading(false);
        }
    };

    const handleBankAccountChange = (type) => {
        setBankOption(type);
        if (type?.value === 'connectBankAccount') {
            setTourRun(false);
            handleAddDateModalOpen();
        } else if (type?.value === 'createBankAccount') {
            setTourRun(false);
            setShowManualAccountForm(true);
        } else if (type?.value === 'connectDivvy') {
            setTourRun(false);
            setShowDivvyModal(true);
        } else if (type?.value === 'manageConnections') {
            setTourRun(false);
            setShowManageConnectionsModal(true);
        }
    };

    const onSuccess = async (public_token, metadata) => {
        try {
            setDownloadLoading(true);
            const response = await setAccessToken(
                companyData?.id,
                public_token,
                dayjs(selectedDate).format('YYYY-MM-DD'),
            );
            setSyncAllLoading(true);
            await syncInitialTransactions(null, response.plaid_item_id, companyData?.id);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setDownloadLoading(false);
            window.location.reload();
        }
    };

    const handleStepBefore = (idx) => {
        setCurrentStepIndex(idx);
        dispatch(updateTourStep({ tourId, stepIndex: idx }));
    };

    const handleTourFinish = () => {
        setDropdownOpen(false);
        setTourRun(false);

        if (tutorialPreference === 'skipped' || isSkipped) {
            dispatch(completeTour({ tourId, skipSaving: false }));
            return;
        }

        if (!isNextTourCompleted) {
            dispatch(
                goToNextTour({
                    currentTourId: tourId,
                    nextTourId: TOUR_TYPES.BANK_ACCOUNT,
                }),
            );

            setShowManualAccountForm(true);

            window.history.replaceState({}, '', '/import-bank-transactions');
        } else {
            dispatch(completeTour({ tourId, skipSaving: false }));
        }
    };

    const { open, ready } = usePlaidLink({
        token: linkToken2,
        onSuccess,
    });

    const createPlaidLinkToken = async (dateIn) => {
        try {
            setIsCreateLinkLoading(true);
            const start_date = dayjs(dateIn).format('YYYY-MM-DD');
            const response = await generateLinkToken(start_date);
            setLinkToken2(response);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setIsCreateLinkLoading(false);
            handleAddDateModalClose(false);
        }
    };

    const sync = async () => {
        try {
            setSyncAllLoading(true);
            await syncTransactions(companyData?.id);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleDateChange = (newDate) => {
        if (newDate && dayjs(newDate).isValid()) {
            setSelectedDate(dayjs(newDate));
        } else {
            setSelectedDate(null);
        }
    };

    const handleGetAllBanks = async () => {
        try {
            setBankAccountsLoading(true);
            const response = await getBankAccounts(companyData?.id);
            setAccounts(response);
            if (response && response?.length) {
                await handleGetBankSyncStatus();
                setSelectedBank(response[0]);
            }
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setBankAccountsLoading(false);
            setDownloadLoading(false);
        }
    };

    const handleManualBankSuccess = (message) => {
        setBankOption(null);
        handleGetAllBanks();
        setShowManualAccountForm(false);
        handleSuccessAlert(setAlertData, message);
    };

    const handleManualBankCloseModal = () => {
        setBankOption(null);
        setShowManualAccountForm(false);
    };

    const handleCloseDivvyModal = () => {
        setBankOption(null);
        setShowDivvyModal(false);
    };

    const handleCloseManageConnectionsModal = () => {
        setBankOption(null);
        setShowManageConnectionsModal(false);
    };

    const handleDivvySuccess = async (message) => {
        try {
            handleCloseDivvyModal();
            handleSuccessAlert(setAlertData, message);
            setSyncAllLoading(true);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleBulkAccountUpdateSuccess = async (message) => {
        try {
            handleSuccessAlert(setAlertData, message);
            setShowAllBanksUpdateForm(false);
            handleGetAllBanks();
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    useEffect(() => {
        if (ready) {
            open();
        }
    }, [ready, open]);

    useEffect(() => {
        if (companyData?.id) {
            handleGetAllBanks();
        }
    }, [companyData]);

    useEffect(() => {
        const manualRestart = tourStatus?.manuallyStarted;

        if (tutorialPreference === 'skipped' || isSkipped) {
            setTourRun(false);
        }

        if (manualRestart) {
            setCurrentStepIndex(0);
            dispatch(startTourManually({ tourId }));
            setTourRun(true);
            setDropdownOpen(true);
        } else if (!tourStatus?.completed && !manualRestart) {
            if (tutorialPreference === 'skipped' || isSkipped) {
                setTourRun(false);
                return;
            }
            const timer = setTimeout(() => {
                setCurrentStepIndex(0);
                dispatch(startTour({ tourId }));
                setTourRun(true);
                setDropdownOpen(true);
            }, 1000);
            return () => clearTimeout(timer);
        } else {
            setTourRun(false);
        }
    }, [tourId, tourStatus?.completed, tourStatus?.manuallyStarted]);

    useEffect(() => {
        if (location.state?.openManualBankModal) {
            setShowManualAccountForm(true);

            window.history.replaceState({}, '', '/import-bank-transactions');
        }
    }, [location.state]);

    useEffect(() => {
        if (!companyData) return;

        const socket = io(SOCKET_URL, {
            transports: ['websocket'],
            path: '/api/socket/',
            query: {
                token: TOKEN,
            },
        });

        const eventName = `sync-status/${companyData.id}/`;

        socket.off(eventName);
        socket.on(eventName, (data) => {
            if (data.status == 'completed') {
                handleSuccessAlert(setAlertData, 'Sync completed successfully');
                setSyncAllLoading(false);
                handleGetAllBanks();
            }
        });

        socket.on('connect_error', (error) => {
            console.error('Connection error:', error);
        });

        return () => {
            socket.disconnect();
        };
    }, [companyData]);

    return (
        <Grid
            item
            p={2}
            xs={12}
            borderRadius={3}
            sx={{
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#212936' : 'white'),
            }}
        >
            <JoyrideTour
                steps={steps}
                run={tourRun}
                continuous={steps.length > 1}
                onStepBefore={handleStepBefore}
                onFinish={handleTourFinish}
                callback={(event) => {
                    if (event.action === 'skip' || event.type === 'tour:end') {
                        setTourRun(false);
                        setDropdownOpen(false);

                        handleTourFinish();
                    }
                }}
                showSkipButton={true}
                showProgress={steps.length > 1}
                stepIndex={currentStepIndex}
                hideCloseButton={true}
                spotlightClicks={true}
            />
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    marginBottom: isMobile ? 0 : 4,
                    justifyContent: 'space-between',
                    flexDirection: isMobile ? 'column' : 'row',
                }}
            >
                <Typography
                    ml={isMobile ? 1.8 : 0}
                    pb={2}
                    sx={{ fontWeight: '800', fontSize: '20px' }}
                >
                    List of bank accounts
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? '10px' : 0,
                    }}
                >
                    <Box className="bank-dropdown-container" data-tour="add-bank-account">
                        <Autocomplete
                            fullWidth
                            options={accountTypes}
                            labelKey="label"
                            valueKey="value"
                            placeholder="Add a Bank Account"
                            open={dropdownOpen || (tourRun && currentStepIndex === 0)}
                            onOpen={() => {
                                setDropdownOpen(true);
                                if (tourRun && currentStepIndex === 0) {
                                    setCurrentStepIndex(1);
                                }
                            }}
                            onClose={() => {
                                setDropdownOpen(false);
                                if (tourRun && currentStepIndex === 0) {
                                    setCurrentStepIndex(1);
                                }
                            }}
                            onChange={(e, value) => handleBankAccountChange(value)}
                            value={bankOption}
                            isSearchField={false}
                            customStyle={classes.dropdown}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Add a Bank Account" />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} data-tour={option['data-tour']}>
                                    {option.label}
                                </li>
                            )}
                        />
                    </Box>
                    {accounts && accounts?.length > 0 && (
                        <Button
                            sx={classes.updateButton}
                            onClick={() => setShowAllBanksUpdateForm(true)}
                        >
                            <BorderColorOutlinedIcon fontSize="small" />
                        </Button>
                    )}
                    {accounts && accounts?.length > 0 && (
                        <LoadingButton
                            startIcon={<SyncIcon />}
                            sx={classes.syncAllButton}
                            onClick={sync}
                            loading={syncAllLoading}
                        >
                            Sync All
                        </LoadingButton>
                    )}
                </Box>
            </Box>

            {bankAccountsLoading || !companyData ? (
                <CircleLoading />
            ) : accounts && accounts?.length ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <BankCardCarousel
                        bankCardsData={accounts}
                        selectedBank={selectedBank}
                        setSelectedBank={setSelectedBank}
                        handleEditBankSuccess={handleManualBankSuccess}
                    />

                    <BankTransactionsStack selectedBank={selectedBank} />
                </Box>
            ) : (
                <Box display="flex" alignItems="center" marginBottom="10px" justifyContent="center">
                    <Typography>
                        {syncAllLoading
                            ? 'Downloading your bank activities, this typically takes a few minutes.'
                            : 'No bank accounts connected yet.'}
                    </Typography>
                </Box>
            )}

            {downloadLoading && <Animation title={`Downloading your bank activities, `} />}

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />

            <AddBankStartDateModal
                open={isAddDateModalOpen}
                selectedDate={selectedDate}
                onClose={handleAddDateModalClose}
                handleDateChange={handleDateChange}
                isCreateLinkLoading={isCreateLinkLoading}
                createPlaidLinkToken={createPlaidLinkToken}
            />

            {showManualAccountForm && (
                <ManualBankForm
                    type="Add"
                    open={showManualAccountForm}
                    handleCloseModal={handleManualBankCloseModal}
                    handleManualBankSuccess={handleManualBankSuccess}
                />
            )}

            {showDivvyModal && (
                <DivvyModal
                    open={showDivvyModal}
                    handleDivvySuccess={handleDivvySuccess}
                    handleCloseModal={handleCloseDivvyModal}
                />
            )}

            {showManageConnectionsModal && (
                <ManageConnectionsModal
                    open={showManageConnectionsModal}
                    handleCloseModal={handleCloseManageConnectionsModal}
                />
            )}

            {showAllBanksUpdateForm && (
                <AllBanksUpdateForm
                    accounts={accounts}
                    setShowAllBanksUpdateForm={setShowAllBanksUpdateForm}
                    handleBulkAccountUpdateSuccess={handleBulkAccountUpdateSuccess}
                />
            )}
        </Grid>
    );
};

export default ImportBankTransaction;
